import { TPatronLevel } from '../common/patron';

export enum RecruitmentActivityType {
  GuildRaids = 'guild_raids',
  GuildPvp = 'guild_pvp',
  GuildSocial = 'guild_social',
  GuildMythicPlus = 'guild_mplus',
  TeamMythicPlus = 'team_mplus'
}

export const enum RecruitmentProfileRequirementType {
  GuildRank = 'guild_rank',
  Progress = 'progress',
  ItemLevel = 'item_level',
  FoS = 'fos',
  IoScore = 'io_score',
  Glory = 'glory',
  CharacterLevel = 'character_level'
}

export const enum RequirementExtentType {
  NONE = 0,
  MINIMUM = 1, // treat this as a minimum only
  MAXIMUM = 2 // treat this as a maximum only
}

export const enum RequirementDataType {
  INTEGER_RANGE = 'integer_range',
  INTEGER = 'integer',
  BOOLEAN = 'boolean'
}
export enum RecruitmentActivationStatus {
  Draft = 'draft',
  Active = 'active',
  Expired = 'expired'
}

export const enum RecruitmentPrivacyMode {
  Private = 'private',
  Public = 'public'
}

export enum RecruitmentEntityType {
  Character = 'character',
  Guild = 'guild',
  Team = 'mplus_team'
}

export const enum RecruitmentApplicationStatus {
  Open = 'open',
  Withdrawn = 'withdrawn',
  Accepted = 'accepted',
  Declined = 'declined',
  Expired = 'expired'
}

export const enum RecruitmentScheduleType {
  Custom = 'custom',
  Weekends = 'weekends',
  Weekdays = 'weekdays',
  Any = 'any',
  None = 'none'
}

export type TRecruitmentRequirement = {
  id: number;
  name: string;
  description: string;
  raid: string;
  season: string;
  category: string;
  difficulty?: string;
  scope?: string;
  icon?: string;

  type: string;

  min_value_label: string;
  max_value_label: string;
  // where this requirement can be used
  // ******************************************
  // NOTE: These must be in sync with the values from RecruitmentEntityType
  // ******************************************
  supports_entity_character: boolean;
  supports_entity_guild: boolean;
  supports_entity_mplus_team: boolean;

  // ******************************************
  // NOTE: These must be in sync with the values from RecruitmentActivityType
  // ******************************************
  supports_activity_guild_raids: boolean;
  supports_activity_guild_mplus: boolean;
  supports_activity_guild_pvp: boolean;
  supports_activity_guild_social: boolean;
  supports_activity_team_mplus: boolean;

  // what user needs to set for these fields
  data_type: RequirementDataType;
  data_min_value: number;
  data_max_value: number;
  data_extent_type: RequirementExtentType;

  // user set fields
  min_value?: number;
  max_value?: number;
};

export type TRecruitmentScheduleDay = {
  day_of_week: number;
  start_minutes: number;
  end_minutes: number;
  is_optional: boolean;
};

export type TProfileSchedule = {
  day_of_week: number;
  start_minutes: number;
  end_minutes: number;
  is_optional: boolean;
};

export type TProfileCharacter = any;

export type TRecruitmentTag = {
  id: number;
  name: string;
  description: string;
  slug: string;
  category: string;

  // ******************************************
  // NOTE: These must be in sync with the values from RecruitmentEntityType
  // ******************************************
  supports_entity_character: boolean;
  supports_entity_guild: boolean;
  supports_entity_mplus_team: boolean;

  // ******************************************
  // NOTE: These must be in sync with the values from RecruitmentActivityType
  // ******************************************
  supports_activity_guild_raids: boolean;
  supports_activity_guild_mplus: boolean;
  supports_activity_guild_pvp: boolean;
  supports_activity_guild_social: boolean;
  supports_activity_team_mplus: boolean;
};

export type TGetRecruitmentProfilesQuery = {
  region?: string | null;
  limit: number;
};

export type TProfileClassEntry = {
  type: 'class';
  class_id?: number | null;
  mainspec_0_id?: number | null;
  mainspec_1_id?: number | null;
  mainspec_2_id?: number | null;
  mainspec_3_id?: number | null;
  offspec_0_id?: number | null;
  offspec_1_id?: number | null;
  offspec_2_id?: number | null;
  offspec_3_id?: number | null;

  // for classic
  mainrole_0?: string | null;
  mainrole_1?: string | null;
  mainrole_2?: string | null;
  mainrole_3?: string | null;
  offrole_0?: string | null;
  offrole_1?: string | null;
  offrole_2?: string | null;
  offrole_3?: string | null;
};

export type TRecruitmentAvailability = {
  type: RecruitmentScheduleType;
  days: TRecruitmentScheduleDay[];
};

export type TRecruitmentProfilePermissions = {
  canView: boolean;
  canEdit: boolean;
  canDelete: boolean;
};
export type TRecruitmentProfileStaticData = any;
// tag payload type

export type TProfileTag = {
  id: number;
};

export type TRequirementCheck = {
  type: string;
  subtype?: string;
  valid?: boolean;
  label?: string;
};

export type TProfileSummary = {
  recruitment_profile_id: number;
  entity_type: RecruitmentEntityType;
  activity_type: RecruitmentActivityType;
};
export type TGetRecruitmentProfilesPayload = {
  regionId?: number | null;
  limit: number;
  subRegionId?: number | null;
  entityType?: RecruitmentEntityType;
};
export type TFeaturedProfilesOptions = {
  productCode: string;
  seed?: string;
} & TGetRecruitmentProfilesPayload; // each slot will have one or more entries that indicate the actual
// roles or classes they are wanting to fill that slot with

export type TProfileSlotEntry = TProfileSlotRoleEntry | TProfileClassEntry; // each slot will have one or more entries that indicate the actual
// roles or classes they are wanting to fill that slot with

export type TProfileSlot = {
  ordinal: number; // for sorting the slots
  priority: number; // 0 = not set, 1 = low, 2 = high
  quantity: number; // how many of these slot they are recruiting
  entries: TProfileSlotEntry[];
};

export type TProfileSlotRoleEntry = {
  type: 'role';
  // dps-melee and dps-ranged used on classic
  role: 'dps' | 'melee-dps' | 'ranged-dps' | 'tank' | 'healer' | 'dps-melee' | 'dps-ranged';
}; // the "slot" represents how many players they are wanting// the "slot" represents how many players they are wanting

export type TBaseRecruitmentProfile = {
  id: number;
  caption?: string;
  schedule_timezone?: string;
  privacy_mode: string;
  published_at?: string;
  expires_at?: string;
  status?: string;
  entity_type: string;
  language_0?: string;
  language_1?: string;
  language_2?: string;
  patronLevel?: TPatronLevel;

  commitment_days_min?: number;
  commitment_days_max?: number;
  commitment_hours_min?: number;
  commitment_hours_max?: number;

  is_cross_realm_allowed?: boolean | null;
  is_realm_transfer_allowed?: boolean | null;
  is_faction_transfer_allowed?: boolean | null;

  requirements: TRecruitmentRequirement[];
  schedule: TRecruitmentScheduleDay[];
  tags: TRecruitmentProfileTag[];

  scheduleType: string;
  activity_type: string;
};

export type TRecruitmentProfileTag = {
  id: number;
  name: string;
};

export enum RecruitmentScheduleDay {
  AnyDay = 'anyday',
  Weekdays = 'weekdays',
  Weekends = 'weekends',
  Sunday = 0,
  Monday = 1,
  Tuesday = 2,
  Wednesday = 3,
  Thursday = 4,
  Friday = 5,
  Saturday = 6
}

export enum RecruitmentScheduleRelation {
  Intersects = 'intersects',
  Within = 'within'
}
